/**
 * OpenAI Proxy API Service
 * Secure client-side service that calls OpenAI through our backend proxy
 * This ensures API keys are never exposed in the frontend
 */

import { auth } from '@/config/firebase';
import axios from 'axios';

// Base URL for the functions endpoint
const FUNCTIONS_BASE_URL = 'https://us-central1-mattressai2.cloudfunctions.net';

/**
 * Get authorization header with current user token
 */
const getAuthHeaders = async () => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('No authenticated user');
  }
  
  const token = await user.getIdToken();
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

/**
 * OpenAI Proxy Service Class
 * Replaces direct OpenAI API calls with secure backend calls
 */
export class OpenAIProxyService {
  
  /**
   * Create an OpenAI assistant
   */
  async createAssistant(config: {
    name: string;
    instructions: string;
    model?: string;
  }): Promise<{ id: string; model: string }> {
    const headers = await getAuthHeaders();
    
    const response = await axios.post(
      `${FUNCTIONS_BASE_URL}/openaiProxy/assistants/create`,
      config,
      { headers }
    );
    
    return response.data;
  }

  /**
   * Update an existing OpenAI assistant
   */
  async updateAssistant(assistantId: string, config: {
    name: string;
    instructions: string;
    model?: string;
  }): Promise<{ id: string; model: string }> {
    const headers = await getAuthHeaders();
    
    const response = await axios.post(
      `${FUNCTIONS_BASE_URL}/openaiProxy/assistants/update`,
      { assistantId, ...config },
      { headers }
    );
    
    return response.data;
  }

  /**
   * Create a thread for conversation
   */
  async createThread(): Promise<{ id: string }> {
    const headers = await getAuthHeaders();
    
    const response = await axios.post(
      `${FUNCTIONS_BASE_URL}/openaiProxy/threads/create`,
      {},
      { headers }
    );
    
    return response.data;
  }

  /**
   * Add a message to a thread
   */
  async addMessageToThread(threadId: string, message: string): Promise<{ id: string }> {
    const headers = await getAuthHeaders();
    
    const response = await axios.post(
      `${FUNCTIONS_BASE_URL}/openaiProxy/threads/messages`,
      { threadId, message },
      { headers }
    );
    
    return response.data;
  }

  /**
   * Create a run for a thread
   */
  async createThreadRun(threadId: string, assistantId: string): Promise<{ id: string; status: string }> {
    const headers = await getAuthHeaders();
    
    const response = await axios.post(
      `${FUNCTIONS_BASE_URL}/openaiProxy/threads/runs/create`,
      { threadId, assistantId },
      { headers }
    );
    
    return response.data;
  }

  /**
   * Get the status of a run
   */
  async getRunStatus(threadId: string, runId: string): Promise<{ status: string }> {
    const headers = await getAuthHeaders();
    
    const response = await axios.get(
      `${FUNCTIONS_BASE_URL}/openaiProxy/threads/runs/status?threadId=${threadId}&runId=${runId}`,
      { headers }
    );
    
    return response.data;
  }

  /**
   * List messages from a thread
   */
  async listThreadMessages(threadId: string, limit: number = 1): Promise<{
    data: Array<{
      id: string;
      content: Array<{
        type: string;
        text?: { value: string };
      }>;
    }>;
  }> {
    const headers = await getAuthHeaders();
    
    const response = await axios.get(
      `${FUNCTIONS_BASE_URL}/openaiProxy/threads/messages/list?threadId=${threadId}&limit=${limit}`,
      { headers }
    );
    
    return response.data;
  }

  /**
   * Analyze an image using GPT-4o Vision
   */
  async analyzeImage(imageUrl: string, prompt?: string): Promise<{
    description: string;
    detectedItems: string[];
    storeRelevance: 'high' | 'medium' | 'low';
  }> {
    const headers = await getAuthHeaders();
    
    const response = await axios.post(
      `${FUNCTIONS_BASE_URL}/openaiProxy/image-analysis`,
      { imageUrl, prompt },
      { headers }
    );
    
    return response.data;
  }

  /**
   * Stream responses from an assistant run
   * Polls the backend for updates instead of true streaming
   */
  async streamThreadResponse(
    threadId: string,
    runId: string,
    callbacks: {
      onMessage: (message: string) => void;
      onError: (error: Error) => void;
      onComplete: () => void;
      onTyping?: (isTyping: boolean) => void;
    }
  ): Promise<void> {
    let lastMessageId: string | null = null;
    const checkInterval = 500; // 500ms between checks
    const maxAttempts = 60; // 30 seconds max wait
    let attempts = 0;

    const checkRun = async () => {
      try {
        const runStatus = await this.getRunStatus(threadId, runId);

        switch (runStatus.status) {
          case 'completed': {
            const messages = await this.listThreadMessages(threadId, 1);
            const latestMessage = messages.data[0];
            
            if (latestMessage && latestMessage.id !== lastMessageId) {
              lastMessageId = latestMessage.id;
              
              const content = latestMessage.content[0];
              if (content.type === 'text' && content.text) {
                callbacks.onMessage(content.text.value);
              }
            }

            callbacks.onTyping?.(false);
            callbacks.onComplete();
            return;
          }

          case 'failed':
          case 'cancelled':
          case 'expired':
            callbacks.onTyping?.(false);
            callbacks.onError(new Error(`Run ${runStatus.status}`));
            return;

          case 'in_progress':
          case 'queued':
            callbacks.onTyping?.(true);
            break;
        }

        attempts++;
        if (attempts >= maxAttempts) {
          callbacks.onTyping?.(false);
          callbacks.onError(new Error('Timeout waiting for response'));
          return;
        }

        // Continue polling
        setTimeout(checkRun, checkInterval);
      } catch (error) {
        callbacks.onTyping?.(false);
        callbacks.onError(error instanceof Error ? error : new Error('Unknown error'));
      }
    };

    // Start polling
    checkRun();
  }

  /**
   * Chat completion (for direct chat without assistants)
   */
  async createChatCompletion(config: {
    messages: Array<{ role: string; content: string }>;
    model?: string;
    temperature?: number;
    max_tokens?: number;
  }): Promise<any> {
    const headers = await getAuthHeaders();
    
    const response = await axios.post(
      `${FUNCTIONS_BASE_URL}/openaiProxy/chat/completions`,
      config,
      { headers }
    );
    
    return response.data;
  }
} 